import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import { createClient } from '@/lib/supabase/client';

// Define the integration keys for consistent reference
export const INTEGRATION_KEYS = {
  WWISE: 'wwise',
  UNITY: 'unity',
  UNREAL: 'unreal',
  FMOD: 'fmod',
  DROPBOX: 'dropbox',
  ADOBE: 'adobe',
  SLACK: 'slack',
  NOTION: 'notion'
};

// Create the context
const IntegrationsContext = createContext();

// Provider component
export const IntegrationsProvider = ({ children }) => {
  // Initialize state for integrations
  const [integrationStatus, setIntegrationStatus] = useState({
    [INTEGRATION_KEYS.WWISE]: false,
    [INTEGRATION_KEYS.UNITY]: false,
    [INTEGRATION_KEYS.UNREAL]: false,
    [INTEGRATION_KEYS.FMOD]: false,
    [INTEGRATION_KEYS.DROPBOX]: false,
    [INTEGRATION_KEYS.ADOBE]: false,
    [INTEGRATION_KEYS.SLACK]: false,
    [INTEGRATION_KEYS.NOTION]: false
  });

  // Load saved integration status from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedStatus = localStorage.getItem('integrationStatus');
      if (savedStatus) {
        setIntegrationStatus(JSON.parse(savedStatus));
      }
    }
  }, []);

  // Save integration status to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('integrationStatus', JSON.stringify(integrationStatus));
    }
  }, [integrationStatus]);

  // Update a specific integration's status
  const updateIntegrationStatus = (key, value) => {
    setIntegrationStatus(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Get the integration status by key
  const getIntegrationStatus = (key) => {
    return integrationStatus[key] || false;
  };

  // Unity integration specific states and functions
  const [unityIntegration, setUnityIntegration] = useState({
    isConnected: false,
    projectPath: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  const { session } = useAuthContext();
  const firebaseUser = session?.user;
  const supabase = createClient();

  // Load initial state from localStorage and user profile
  useEffect(() => {
    if (!firebaseUser) {
      setIsLoading(false);
      return;
    }

    // Load from localStorage first for immediate UI response
    const unityConnected = localStorage.getItem('unity_connected') === 'true';
    const unityProjectPath = localStorage.getItem('unity_project_path') || '';

    setUnityIntegration({
      isConnected: unityConnected,
      projectPath: unityProjectPath
    });

    // Then load from user profile if available (for consistency)
    const loadFromUserProfile = async () => {
      try {
        // Use user_metadata for integrations
        const integrations = firebaseUser?.user_metadata?.integrations;

        if (integrations?.unity) {
          const profileUnityData = integrations.unity;

          // Update state and localStorage if profile data is different
          if (profileUnityData.isConnected !== unityConnected ||
            profileUnityData.projectPath !== unityProjectPath) {

            setUnityIntegration({
              isConnected: profileUnityData.isConnected,
              projectPath: profileUnityData.projectPath || ''
            });

            // Update localStorage to match profile
            localStorage.setItem('unity_connected', profileUnityData.isConnected ? 'true' : 'false');
            localStorage.setItem('unity_project_path', profileUnityData.projectPath || '');
          }
        }
      } catch (error) {
        // console.error('Error loading integration status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFromUserProfile();
  }, [firebaseUser]);

  // Connect Unity function
  const connectUnity = async (projectPath) => {
    if (!projectPath) return;

    // Update state
    setUnityIntegration({
      isConnected: true,
      projectPath
    });

    // Update integration status
    updateIntegrationStatus(INTEGRATION_KEYS.UNITY, true);

    // Update localStorage
    localStorage.setItem('unity_connected', 'true');
    localStorage.setItem('unity_project_path', projectPath);

    // Remove any old keys for consistency
    localStorage.removeItem('unityProjectPath');

    // Update user profile in Supabase
    try {
      if (firebaseUser) {
        const currentMetadata = firebaseUser.user_metadata || {};
        const currentIntegrations = currentMetadata.integrations || {};

        const newIntegrations = {
          ...currentIntegrations,
          unity: {
            isConnected: true,
            projectPath
          }
        };

        await supabase.auth.updateUser({
          data: {
            integrations: newIntegrations
          }
        });
        // console.log('Unity integration saved to profile');
      }
    } catch (error) {
      // console.error('Error updating profile with Unity integration:', error);
    }
  };

  // Disconnect Unity function
  const disconnectUnity = async () => {
    // Update state
    setUnityIntegration({
      isConnected: false,
      projectPath: ''
    });

    // Update integration status
    updateIntegrationStatus(INTEGRATION_KEYS.UNITY, false);

    // Update localStorage
    localStorage.setItem('unity_connected', 'false');
    localStorage.removeItem('unity_project_path');

    // Remove any old keys for consistency
    localStorage.removeItem('unityProjectPath');

    // Update user profile in Supabase
    try {
      if (firebaseUser) {
        const currentMetadata = firebaseUser.user_metadata || {};
        const currentIntegrations = currentMetadata.integrations || {};

        const newIntegrations = {
          ...currentIntegrations,
          unity: {
            isConnected: false,
            projectPath: ''
          }
        };

        await supabase.auth.updateUser({
          data: {
            integrations: newIntegrations
          }
        });
        // console.log('Unity integration disconnected in profile');
      }
    } catch (error) {
      // console.error('Error updating profile with Unity integration:', error);
    }
  };

  // Combined context value with all integration data
  const value = {
    // General integration status
    integrationStatus,
    updateIntegrationStatus,
    getIntegrationStatus,

    // Unity specific
    unityIntegration,
    isLoading,
    connectUnity,
    disconnectUnity
  };

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};

// Custom hook to use the integrations context
export const useIntegrations = () => {
  const context = useContext(IntegrationsContext);
  if (context === undefined) {
    throw new Error('useIntegrations must be used within an IntegrationsProvider');
  }
  return context;
}; 